import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_BY_TRIAL_GROUP_LABORATORY_ID,
    ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_BY_ID,
    ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_FAILURE,
    ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_SUCCESS,

    ACTION_TRIALGROUPLABORATORYCONTRACT_SAVE,
    ACTION_TRIALGROUPLABORATORYCONTRACT_SAVE_SUCCESS,
    ACTION_TRIALGROUPLABORATORYCONTRACT_SAVE_FAILURE,

    ACTION_TRIALGROUPLABORATORYCONTRACT_VALIDATE,
    ACTION_TRIALGROUPLABORATORYCONTRACT_VALIDATE_SUCCESS,
    ACTION_TRIALGROUPLABORATORYCONTRACT_VALIDATE_FAILURE,

    ACTION_TRIALGROUPLABORATORYCONTRACT_CREATE,
    ACTION_TRIALGROUPLABORATORYCONTRACT_CREATE_SUCCESS,
    ACTION_TRIALGROUPLABORATORYCONTRACT_CREATE_FAILURE,

    ACTION_TRIALGROUPLABORATORYCONTRACT_DELETE,
    ACTION_TRIALGROUPLABORATORYCONTRACT_DELETE_SUCCESS,
    ACTION_TRIALGROUPLABORATORYCONTRACT_DELETE_FAILURE,

    ACTION_TRIALGROUPLABORATORYCONTRACT_CLEAR,
    ACTION_TRIALGROUPLABORATORYCONTRACT_FILTER,

    TrialGroupLaboratoryContractAction
} from '../actions/trialGroupLaboratoryContract';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialGroupLaboratoryContractState {
    formData: Dtos.TrialGroupLaboratoryContract | Dtos.TrialGroupLaboratoryContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    deleteState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialGroupLaboratoryContract[];
    formLength: number;
}

const initialState: ITrialGroupLaboratoryContractState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    deleteState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialGroupLaboratoryContractState, _isLoading: boolean): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialGroupLaboratoryContractState,
    formData: Dtos.TrialGroupLaboratoryContract | Dtos.TrialGroupLaboratoryContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialGroupLaboratoryContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialGroupLaboratoryContractState, formFilter: IFormFilter): ITrialGroupLaboratoryContractState {

    const items: Dtos.TrialGroupLaboratoryContract[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialGroupLaboratoryContractState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialGroupLaboratoryContractState, propertiesToClear?: string[]): ITrialGroupLaboratoryContractState {

    if (!propertiesToClear) {
        const newState: ITrialGroupLaboratoryContractState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialGroupLaboratoryContractState, _isSaving: boolean): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialGroupLaboratoryContractState,
    formData: Dtos.TrialGroupLaboratoryContract | Dtos.TrialGroupLaboratoryContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialGroupLaboratoryContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialGroupLaboratoryContractState, _isValidating: boolean): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialGroupLaboratoryContractState,
    formData: Dtos.TrialGroupLaboratoryContract | Dtos.TrialGroupLaboratoryContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialGroupLaboratoryContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialGroupLaboratoryContractState, _isCreating: boolean): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialGroupLaboratoryContractState,
    formData: Dtos.TrialGroupLaboratoryContract,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialGroupLaboratoryContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function deleting(state: ITrialGroupLaboratoryContractState, _isDeleting: boolean): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function deleteSuccess(
    state: ITrialGroupLaboratoryContractState,
): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function deleteFailure(state: ITrialGroupLaboratoryContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryContractState {
    const newState: ITrialGroupLaboratoryContractState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialGroupLaboratoryContractReducer: Reducer<ITrialGroupLaboratoryContractState> = (state: ITrialGroupLaboratoryContractState = initialState, action: TrialGroupLaboratoryContractAction) => {
    switch (action.type) {
        case ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_BY_TRIAL_GROUP_LABORATORY_ID:
        case ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_SAVE:
            return saving(state, true);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_CREATE:
            return creating(state, true);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_DELETE:
            return deleting(state, true);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_DELETE_SUCCESS:
            return deleteSuccess(state);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_DELETE_FAILURE:
            return deleteFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYCONTRACT_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TrialGroupLaboratoryContractReducer;